import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { invoicingService } from '../../services/invoicingService';
import type { BusinessProfileDetail } from '../../types/businessProfile';

interface ConsolidatedInvoicingToggleProps {
  businessProfile: BusinessProfileDetail;
  onProfileUpdate: () => void;
}

// SW-90 on/off switch — a one-off setting, so it lives in Konfiguracja next
// to KSeF. The day-to-day invoicing screen stays in InvoicingSection (Operacje).
export function ConsolidatedInvoicingToggle({
  businessProfile,
  onProfileUpdate,
}: ConsolidatedInvoicingToggleProps) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { showToast } = useToast();

  const [isToggling, setIsToggling] = useState(false);

  const isEnabled = businessProfile.consolidatedInvoicingEnabled || false;

  const handleToggleEnabled = async () => {
    if (!token || !businessProfile.id) return;

    setIsToggling(true);
    try {
      await invoicingService.setConsolidatedInvoicingEnabled(businessProfile.id, !isEnabled, token);
      showToast(
        'success',
        !isEnabled
          ? t('invoicing.enabled', 'Fakturowanie zbiorcze włączone')
          : t('invoicing.disabled', 'Fakturowanie zbiorcze wyłączone')
      );
      onProfileUpdate();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to toggle consolidated invoicing';
      showToast('error', t('common.error'), message);
    } finally {
      setIsToggling(false);
    }
  };

  return (
    <div className="bg-surface-card rounded-lg p-4 flex items-start justify-between gap-4">
      <div className="min-w-0">
        <p className="text-sm font-medium text-ink-primary">
          {t('invoicing.title', 'Fakturowanie zbiorcze')}
        </p>
        <p className="text-sm text-ink-secondary mt-1">
          {t(
            'invoicing.toggleDescription',
            'Aktywuj, jeśli chcesz wystawiać faktury manualnie dla wybranych klientów.'
          )}
        </p>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="text-sm text-ink-primary">
          {isEnabled ? t('invoicing.enabledLabel', 'Włączone') : t('invoicing.disabledLabel', 'Wyłączone')}
        </span>
        <button
          onClick={handleToggleEnabled}
          disabled={isToggling}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 focus:ring-offset-surface-card ${isEnabled ? 'bg-brand-primary' : 'bg-surface-app-elevated'} ${isToggling ? 'opacity-50 cursor-not-allowed' : ''}`}
          role="switch"
          aria-checked={isEnabled}
          aria-label={t('invoicing.title', 'Fakturowanie zbiorcze')}
        >
          <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isEnabled ? 'translate-x-5' : 'translate-x-0'}`}
          />
        </button>
      </div>
    </div>
  );
}
